import { BOX_CATEGORIES, BOX_CATEGORY_INFO } from "@loot-os/api-contract";
import {
  AVAILABILITY_STRATEGIES,
  PRICING_SOURCES,
  type PricingSource,
} from "@loot-os/fulfillment-core/catalog/item";
import { printingsForCardGame, TCG_CARD_GAMES } from "@loot-os/fulfillment-core/catalog/tcg-import";
import { Button } from "@loot-os/ui/components/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { RasterImageField } from "../../components/StorefrontFields";
import {
  CATALOG_ITEM_KIND_LABELS,
  CATALOG_ITEM_KINDS,
  CATALOG_ITEM_STATUSES,
  CIRCULATION_OVERRIDES,
  isMember,
  suggestedValueDollars,
} from "../../lib/catalogItemCreate";
import {
  type CatalogItemEditDraft,
  type CatalogItemEditErrors,
  type CatalogItemEditRecord,
  draftFromCatalogItem,
  toCatalogItemUpdate,
} from "../../lib/catalogItemEdit";
import { uploadTenantAssetFile } from "../../lib/tenantAssets";
import { useTRPC } from "../../lib/trpc";

const NONE = "NONE";

const KIND_LABELS: Record<string, string> = {
  UNSET: "Unset",
  ...CATALOG_ITEM_KIND_LABELS,
};
const PRICING_LABELS: Record<PricingSource, string> = {
  TCGPLAYER: "TCGplayer",
  MANUAL: "Manual",
  STOCKX: "StockX",
  FANATICS: "Fanatics",
  SHOPIFY: "Shopify",
  OTHER: "Other",
};
const STRATEGY_LABELS = { DYNAMIC: "Dynamic", FIXED: "Fixed" };
const STATUS_LABELS = {
  STANDARD: "Standard",
  PRE_RELEASE: "Pre-release",
  ARCHIVED: "Archived",
};
const CIRCULATION_LABELS = {
  [NONE]: "Policy default",
  OPEN: "Open",
  OFFICIAL_ONLY: "Official boxes only",
};
const CARD_GAME_LABELS: Record<string, string> = {
  [NONE]: "None",
  ...Object.fromEntries(
    BOX_CATEGORIES.filter((code) => !BOX_CATEGORY_INFO[code].digital).map((code) => [
      code,
      BOX_CATEGORY_INFO[code].label,
    ]),
  ),
};

/** Edit one catalog item. High error rate is a tag; the Roca id is its external id. */
export function CatalogItemPage() {
  const { itemId } = useParams({ from: "/app/fulfillment/items/$itemId" });
  const trpc = useTRPC();
  const item = useQuery(trpc.platform.fulfillment.items.get.queryOptions({ id: itemId }));

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <div>
        <Link
          to="/fulfillment/inventory"
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Inventory
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">
          {item.data?.name ?? "Catalog item"}
        </h1>
      </div>

      {item.isError ? <ErrorAlert message="This catalog item could not be loaded." /> : null}
      {item.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {item.data ? (
        <CatalogItemEditor key={`${item.data.id}:${item.data.updatedAt}`} item={item.data} />
      ) : null}
    </div>
  );
}

function CatalogItemEditor({ item }: { item: CatalogItemEditRecord }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() => draftFromCatalogItem(item));
  const [errors, setErrors] = useState<CatalogItemEditErrors>({});
  const [valueEdited, setValueEdited] = useState(item.value !== null);
  const [imageUploading, setImageUploading] = useState(false);
  const save = useMutation(
    trpc.platform.fulfillment.items.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Saved");
        await queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.items.pathKey(),
        });
      },
    }),
  );

  const update = <K extends keyof CatalogItemEditDraft>(key: K, value: CatalogItemEditDraft[K]) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "cardGame" && typeof value === "string") {
        const nextGame = isMember(TCG_CARD_GAMES, value) ? value : null;
        if (nextGame && next.printing && !printingsForCardGame(nextGame).includes(next.printing)) {
          next.printing = "";
        }
      }
      return next;
    });
    setErrors((prev) => {
      if (prev[key] === undefined && (key !== "cardGame" || prev.printing === undefined))
        return prev;
      const next = { ...prev };
      delete next[key];
      if (key === "cardGame") delete next.printing;
      return next;
    });
  };

  const onMarketPrice = (raw: string) => {
    setDraft((prev) => {
      const next = { ...prev, marketPriceDollars: raw };
      if (!valueEdited) next.valueDollars = suggestedValueDollars(raw) ?? "";
      return next;
    });
    setErrors((prev) => {
      if (prev.marketPriceDollars === undefined && prev.valueDollars === undefined) return prev;
      const next = { ...prev };
      delete next.marketPriceDollars;
      if (!valueEdited) delete next.valueDollars;
      return next;
    });
  };

  function submit() {
    if (save.isPending || imageUploading) return;
    const parsed = toCatalogItemUpdate(item, draft);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    if (!parsed.update) return;
    save.mutate(parsed.update);
  }

  const manual = draft.kind === "SLABBED" || draft.kind === "SEALED" || draft.kind === "OTHER";
  const game = isMember(TCG_CARD_GAMES, draft.cardGame) ? draft.cardGame : null;
  const printingLabels = printingChoices(game, draft.printing);
  const marketPriceReady = suggestedValueDollars(draft.marketPriceDollars) !== null;

  return (
    <form
      className="flex flex-col gap-6"
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Id</dt>
        <dd className="truncate font-mono text-xs">{item.id}</dd>
        <dt className="text-muted-foreground">Fulfillable status</dt>
        <dd>{item.fulfillableStatus}</dd>
        <dt className="text-muted-foreground">Withdrawing</dt>
        <dd>{item.withdrawingCount}</dd>
      </dl>

      {Object.keys(errors).length > 0 ? (
        <p className="text-sm text-destructive" role="alert">
          Fix the fields below before saving.
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <EnumField
          id="catalog-item-kind"
          label="Kind"
          description="Unset leaves a boxed single as a single, and a custom item with no kind under Unclassified."
          value={draft.kind}
          labels={KIND_LABELS}
          onChange={(value) => {
            if (value === "UNSET" || isMember(CATALOG_ITEM_KINDS, value)) update("kind", value);
          }}
        />
        <TextField
          id="catalog-item-name"
          className="sm:col-span-2"
          label="Full name"
          description="The name staff and tenants see."
          error={errors.name}
          value={draft.name}
          onChange={(value) => update("name", value)}
        />
        <TextField
          id="catalog-item-collection"
          className="sm:col-span-2"
          label="Collection name"
          description="The set name, or other collection name."
          error={errors.collectionName}
          value={draft.collectionName}
          onChange={(value) => update("collectionName", value)}
        />
        <TextField
          id="catalog-item-market-price"
          label="Market price"
          description="Purchase price or market price, in dollars."
          error={errors.marketPriceDollars}
          value={draft.marketPriceDollars}
          onChange={onMarketPrice}
          inputMode="decimal"
          placeholder="0.00"
        />
        {marketPriceReady || draft.valueDollars ? (
          <TextField
            id="catalog-item-value"
            label="Value"
            description="In dollars. While value is blank, it fills from 1.25 times the market price until you edit it."
            error={errors.valueDollars}
            value={draft.valueDollars}
            onChange={(value) => {
              setValueEdited(true);
              update("valueDollars", value);
            }}
            inputMode="decimal"
            placeholder="0.00"
          />
        ) : null}
        <TextField
          id="catalog-item-source"
          label="Source id"
          description="The TCGplayer product id."
          error={errors.sourceId}
          value={draft.sourceId}
          onChange={(value) => update("sourceId", value)}
          spellCheck={false}
        />
        <EnumField
          id="catalog-item-game"
          label="Card game"
          description="Saved as the item's game and tag. Other tags stay."
          error={errors.cardGame}
          value={draft.cardGame || NONE}
          labels={CARD_GAME_LABELS}
          onChange={(value) => update("cardGame", value === NONE ? "" : value)}
        />
        {game ? (
          <EnumField
            id="catalog-item-printing"
            label="Card printing"
            error={errors.printing}
            value={draft.printing || NONE}
            labels={printingLabels}
            onChange={(value) => update("printing", value === NONE ? "" : value)}
          />
        ) : (
          <TextField
            id="catalog-item-printing"
            label="Card printing"
            description="Free text when the game has no printing list."
            error={errors.printing}
            value={draft.printing}
            onChange={(value) => update("printing", value)}
          />
        )}
        <div className="sm:col-span-2">
          <CatalogImageField
            value={draft.image}
            uploading={imageUploading}
            disabled={save.isPending}
            onChange={(value) => update("image", value)}
            onUploadingChange={setImageUploading}
          />
        </div>
        <EnumField
          id="catalog-item-pricing"
          label="Pricing source"
          value={draft.pricingSource}
          labels={PRICING_LABELS}
          onChange={(value) => {
            if (isMember(PRICING_SOURCES, value)) update("pricingSource", value);
          }}
        />
        <EnumField
          id="catalog-item-availability"
          label="Availability"
          description={
            manual
              ? "Fixed: a box can contain this only while a unit is on hand."
              : "Dynamic: a unit can be bought when someone needs one."
          }
          value={draft.availabilityStrategy}
          labels={STRATEGY_LABELS}
          onChange={(value) => {
            if (isMember(AVAILABILITY_STRATEGIES, value)) update("availabilityStrategy", value);
          }}
        />
        <EnumField
          id="catalog-item-status"
          label="Item status"
          description="Archived is refused while units are on hand or withdrawing."
          value={draft.status}
          labels={STATUS_LABELS}
          onChange={(value) => {
            if (isMember(CATALOG_ITEM_STATUSES, value)) update("status", value);
          }}
        />
        <EnumField
          id="catalog-item-circulation"
          label="Circulation"
          description="Who else can use this. A pre-release item stays official-only until it is standard."
          value={draft.overrideCirculation || NONE}
          labels={CIRCULATION_LABELS}
          onChange={(value) => {
            update("overrideCirculation", isMember(CIRCULATION_OVERRIDES, value) ? value : "");
          }}
        />
        <TextField
          id="catalog-item-quantity"
          label="On hand"
          description="Units in the facility. Saving sets this count."
          error={errors.quantity}
          value={draft.quantity}
          onChange={(value) => update("quantity", value)}
          inputMode="numeric"
        />
        <TextField
          id="catalog-item-compare-at"
          label="Compare-at value"
          description="A higher struck-through number, in dollars. Must be greater than value."
          error={errors.compareAtValueDollars}
          value={draft.compareAtValueDollars}
          onChange={(value) => update("compareAtValueDollars", value)}
          inputMode="decimal"
          placeholder="0.00"
        />
        <TextField
          id="catalog-item-compare-at-label"
          label="Compare-at label"
          description="Optional label beside that number. At most 40 characters."
          error={errors.compareAtLabelOverride}
          value={draft.compareAtLabelOverride}
          onChange={(value) => update("compareAtLabelOverride", value)}
        />
        <TextField
          id="catalog-item-tenant"
          className="sm:col-span-2"
          label="Tenant"
          description="Blank is the shared catalog. A tenant id binds the item to that tenant."
          value={draft.tenantId}
          onChange={(value) => update("tenantId", value)}
          spellCheck={false}
          inputClassName="font-mono"
        />
      </div>

      <div className="flex flex-col gap-2">
        <Field orientation="horizontal">
          <Switch
            id="catalog-item-high-error"
            checked={draft.highError}
            onCheckedChange={(checked) => update("highError", checked)}
            disabled={save.isPending}
          />
          <FieldLabel htmlFor="catalog-item-high-error">High error rate</FieldLabel>
        </Field>
        <FieldDescription>
          Stored as the HIGH_ERROR tag. The item's other tags stay as they are.
        </FieldDescription>
      </div>

      <Field>
        <FieldLabel htmlFor="catalog-item-roca">Roca ID</FieldLabel>
        <Input
          id="catalog-item-roca"
          value={draft.rocaId}
          onChange={(event) => update("rocaId", event.target.value)}
          disabled={save.isPending}
          autoComplete="off"
        />
        <FieldDescription>
          The item's ROCA row in external product ids. Clear it to remove the id. An id another item
          already has is refused.
        </FieldDescription>
      </Field>

      <Button type="submit" disabled={save.isPending || imageUploading} className="self-start">
        {save.isPending ? "Saving…" : "Save"}
      </Button>
    </form>
  );
}

function printingChoices(game: (typeof TCG_CARD_GAMES)[number] | null, current: string) {
  const labels: Record<string, string> = { [NONE]: "None" };
  if (!game) return labels;
  for (const printing of printingsForCardGame(game)) labels[printing] = printing;
  if (current && labels[current] === undefined) labels[current] = current;
  return labels;
}

function CatalogImageField({
  value,
  uploading,
  disabled,
  onChange,
  onUploadingChange,
}: {
  value: string;
  uploading: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const trpc = useTRPC();
  const available = useQuery(trpc.platform.fulfillment.items.imageUploadsAvailable.queryOptions());
  const startUpload = useMutation(
    trpc.platform.fulfillment.items.startImageUpload.mutationOptions(),
  );
  const finishUpload = useMutation(
    trpc.platform.fulfillment.items.finishImageUpload.mutationOptions(),
  );

  const upload = async (file: File) => {
    onUploadingChange(true);
    try {
      const { publicUrl } = await uploadTenantAssetFile(
        file,
        (input) => startUpload.mutateAsync(input),
        (uploadKey) => finishUpload.mutateAsync({ uploadKey }),
      );
      onChange(publicUrl);
      toast.success("Image uploaded.");
    } finally {
      onUploadingChange(false);
    }
  };

  return (
    <RasterImageField
      id="catalog-item-image"
      label="Image"
      url={value}
      onUrlChange={onChange}
      onFile={upload}
      uploading={uploading}
      disabled={disabled}
      uploadsAvailable={available.data !== false}
      previewClassName="size-full object-contain"
      help="Upload a PNG, JPEG, WEBP or AVIF, or paste a URL. A new image is stored as the override, so the source image stays."
    />
  );
}

function TextField({
  id,
  label,
  description,
  error,
  value,
  onChange,
  className,
  inputClassName,
  spellCheck,
  inputMode,
  placeholder,
}: {
  id: string;
  label: string;
  description?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
  spellCheck?: boolean;
  inputMode?: "decimal" | "numeric";
  placeholder?: string;
}) {
  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <Input
        id={id}
        className={inputClassName}
        value={value}
        spellCheck={spellCheck}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError>{error}</FieldError>
    </Field>
  );
}

function EnumField({
  id,
  label,
  description,
  error,
  value,
  labels,
  onChange,
  className,
}: {
  id: string;
  label: string;
  description?: string;
  error?: string;
  value: string;
  labels: Record<string, string>;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <Select items={labels} value={value || null} onValueChange={(next) => next && onChange(next)}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(labels).map(([option, optionLabel]) => (
            <SelectItem key={option} value={option}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldError>{error}</FieldError>
    </Field>
  );
}
